/**
 * Web stub for expo-linear-gradient.
 *
 * WHY: the published package ships UNTRANSPILED JSX inside `build/*.js`
 * (`return (<NativeLinearGradient ...`), which Vite/Rollup cannot parse —
 * "Expression expected in .../expo-linear-gradient/build/LinearGradient.js".
 * Native (Metro) handles it fine; only the web build trips.
 *
 * This is the same failure mode the config already stubs for
 * `@expo/vector-icons` and `react-native-reanimated` — see the aliases in
 * vite.config.ts. The stub exists so a SHARED (non-`.native`) component can
 * import the gradient without breaking `frontend-tests`.
 *
 * WHAT WEB GETS: a solid fill of the gradient's FIRST colour, which for the
 * Daylight CTA family is `cedarPrime` — i.e. exactly the flat green those
 * buttons had before the gradient landed. So the web prototype is unchanged
 * rather than degraded, and no caller has to branch on Platform.
 *
 * Deliberately NOT a CSS gradient: react-native-web filters unrecognised
 * style props, so `backgroundImage` would need a DOM escape hatch for a
 * surface the platform notes call a legacy prototype. If web ever becomes a
 * first-class target again, this is the one file to upgrade.
 */
import { createElement } from 'react';
import { View, type ViewProps } from 'react-native';

interface LinearGradientProps extends ViewProps {
  colors?: readonly string[];
  start?: { x: number; y: number };
  end?: { x: number; y: number };
  locations?: readonly number[];
}

export function LinearGradient({
  colors,
  start: _start,
  end: _end,
  locations: _locations,
  style,
  children,
  ...rest
}: LinearGradientProps) {
  const fill = colors && colors.length > 0 ? colors[0] : undefined;
  return createElement(
    View,
    { ...rest, style: [style, fill ? { backgroundColor: fill } : null] },
    children,
  );
}

export default LinearGradient;
